import Term     from './glossary/Term'
import TermForm from './glossary/TermForm'

export default class Glossary extends React.PureComponent

  constructor: (props) ->
    super(props)

    @state =
      targetLanguage: @initialTargetLanguage()
      terms:          []
      filteredTerms:  []
      loaded:         false
      query:          ''
      newSource:      ''
      editedTermId:   null # null: no edition | 0: new term | >0: existing term
      flashedTermId:  0
      flashedColor:   'green' # green | yellow

    @dReloadTermsFromBackend = _.debounce(@reloadTermsFromBackend, 5)

    @saveTerm          = @saveTerm.bind(this)
    @deleteTerm        = @deleteTerm.bind(this)
    @editTerm          = @editTerm.bind(this)
    @closeTerm         = @closeTerm.bind(this)
    @updateQuery       = @updateQuery.bind(this)
    @handleEscInSearch = @handleEscInSearch.bind(this)
    @setTargetLanguage = @setTargetLanguage.bind(this)
    @focusSearchInput  = @focusSearchInput.bind(this)

  componentDidMount: ->
    @dReloadTermsFromBackend( =>
      @filterTerms()

      # Scroll to and flash a specific term after modal load
      if @props.flashedTermId != 0
        @setFlashedTermAndScrollToIt(@props.flashedTermId, 'yellow', 100) # short delay, for first render
    )

    @focusSearchInput(450) # short delay necessary because of Bootstrap's modal opening sequence

  componentWillUnmount: ->
    @reloadRequest.abort() if @reloadRequest

  initialTargetLanguage: ->
    targetLanguage = _.find(@props.project.targetLanguages, (language) => 
      language.id == @props.selectedTargetLanguageId
    )

    targetLanguage || @props.project.targetLanguages[0]

  # Backend calls

  backendPath: ->
    "/#{@props.project.owner}/#{@props.project.name}/#{@state.targetLanguage.code}/glossary_terms"

  reloadTermsFromBackend: (callback) ->
    path = @backendPath() # To check if still consistent with state when response arrives

    @reloadRequest = http.get("#{path}.json", {}, (terms) =>
      if path == @backendPath() # Still consistent with state?
        @setState(
          terms:  terms
          loaded: true
        , callback)
    )

  saveTerm: (term, onSuccess, onError) ->
    if term.id
      verb = "put"
      path = "#{@backendPath()}/#{term.id}.json"
    else
      verb = "post"
      path = "#{@backendPath()}.json"

    http[verb](path, { glossaryTerm: term }, (data) =>
      if data.success
        onSuccess()

        @reloadTermsFromBackend( =>
          if verb == 'post' # When creating: remove active filter to always be able to show the new term
            @setState(query: '', @filterTerms)
          else if verb == 'put' # When updating: keep filter and the position of term
            @setState(
              filteredTerms: @state.filteredTerms.map((t, i) =>
                if t.id == term.id then data.glossaryTerm else t
              )
            )

          @setFlashedTermAndScrollToIt(data.glossaryTerm.id, 'green')
        )
      else
        onError(data.errors) # form errors
    , onError.bind(this, ['Something went wrong, please try again.']) # generic error (ex: if updated term was removed by another user)
    )

  deleteTerm: (term) ->
    if window.confirm("Remove “#{term.source}” from the glossary?")
      reload = => @reloadTermsFromBackend(@filterTerms) # Same callback for success or failure (failure could be "already removed")
      http.delete("#{@backendPath()}/#{term.id}.json", {}, reload, reload)

  # Opening/closing term actions

  newTerm: (newSource) ->
    @setState(
      editedTermId: 0
      newSource:    newSource
    )

  editTerm: (term) ->
    @setState(
      editedTermId: term.id
    )

  closeTerm: ->
    @setState(
      editedTermId: null
      newSource:    ''
    , @focusSearchInput)

  # Event handlers

  updateQuery: (e) ->
    @setState(
      query: e.target.value
    , @filterTerms)

  handleEscInSearch: (e) ->
    if e.keyCode == 27 && @state.query.length # only when aleady filtering
      e.stopPropagation() # prevent closing of glossary modal

  setTargetLanguage: (e) ->
    newTargetLanguage = _.find(@props.project.targetLanguages, { id: parseInt(e.target.value) })

    @setState(
      loaded:         false
      targetLanguage: newTargetLanguage
      terms:          []
      filteredTerms:  []
    , =>
      @focusSearchInput()
      @dReloadTermsFromBackend( =>
        @filterTerms()
      )
    )

  focusSearchInput: (delay = 0) ->
    setTimeout( =>
      @refs.searchInput.focus()
    , delay)

  setFlashedTermAndScrollToIt: (termId, flashedColor, scrollDelay = 0) ->
    @setState(
      flashedTermId: termId
      flashedColor:  flashedColor
    , =>
      setTimeout( =>
        if @state.flashedTermId == termId # Ensure the same term is still flashed at that moment
          @setState(flashedTermId: 0)
      , 3000) # bit more than ".flashing" class (2.5s)
    )

    setTimeout( =>
      # For a long list of terms, the "smooth" behavior takes too long
      scrollBehavior = if @state.filteredTerms.length < 100 then "smooth" else "auto"
      
      element = document.getElementById("term-#{termId}")
      parent  = document.getElementById("glossary-terms")
      
      if element && parent
        targetOffset = parent.clientHeight * 0.20
        scrollTop    = element.offsetTop - targetOffset

        parent.scrollTo(top: scrollTop, behavior: scrollBehavior)
    , scrollDelay)

  # Front-end filtering (on source or target, case insensitive)

  editedTerm: ->
    @state.filteredTerms.find((term) => term.id == @state.editedTermId)

  filterTerms: ->
    query = @state.query.trim().toLowerCase()

    if query == ''
      filteredTerms = @state.terms
    else
      filteredTerms = _.filter(@state.terms, (term) =>
        term.source.toLowerCase().indexOf(query) >= 0 ||
        term.target.toLowerCase().indexOf(query) >= 0
      )

    @setState(filteredTerms: filteredTerms)

  languageCodeAndName: (language) ->
    "#{language.code} • #{language.regionName}"

  # Rendering

  render: ->
    <>
      {@renderHeader()}

      <div className="modal-body">
        {@renderToolbar()}
        {@renderBlankSlate()}
        {@renderNoMatch()}
        {@renderTerms()}
      </div>

      {@renderTermForm()}
    </>

  renderHeader: ->
    <div className="modal-header">
      <h5 className="modal-title" id="modal-glossary-label" title={"Glossary for #{@props.project.name}"}>
        Glossary
        <span className="separator">
          {" • "}
        </span>
        <span className="project-name">
          {@props.project.name}
        </span>
      </h5>

      <div className="language-pair">
        <span className="source-language">
          {@props.project.sourceLanguage.code}
        </span>
        <i className="fas fa-angle-right" />
        {@renderTargetLanguageSelect()}
      </div>

      <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
    </div>

  renderTargetLanguageSelect: ->
    <select className="form-select"
            name="select-language"
            title="Target language"
            value={@state.targetLanguage.id}
            onChange={@setTargetLanguage}>
      {@renderTargetLanguageOptions()}
    </select>

  renderTargetLanguageOptions: ->
    _.map(@props.project.targetLanguages, (targetLanguage, index) =>
      <option key={index} value={targetLanguage.id}>
        {@languageCodeAndName(targetLanguage)}
      </option>
    )

  renderToolbar: ->
    <div className="glossary-toolbar">
      <div className="search-menu">
        <i className="fas fa-magnifying-glass" />
        <input type="search"
               ref="searchInput"
               className="form-control"
               placeholder="Search glossary"
               value={@state.query}
               onChange={@updateQuery}
               onKeyDown={@handleEscInSearch} />
      </div>

      <button type="button"
              className="btn btn-primary new-term"
              onClick={@newTerm.bind(this, '')}>
        New term
      </button>
    </div>

  renderBlankSlate: ->
    if @state.loaded && @state.query.trim() == '' && !@state.terms.length
      <div className="empty-container">
        <div className="empty">
          No glossary terms.
          <br/>
          <a href="#"
             className="d-inline-block mt-2"
             onClick={@newTerm.bind(this, '')}>
            New term
          </a>
        </div>
      </div>

  renderNoMatch: ->
    if @state.loaded && @state.query.trim() != '' && !@state.filteredTerms.length
      <div className="empty-container">
        <div className="empty">
          No glossary terms matching <em>{@state.query}</em>.
          <br/>
          <a href="#"
             className="d-inline-block mt-2"
             onClick={@newTerm.bind(this, @state.query)}>
            Add “{@state.query}” to glossary
          </a>
        </div>
      </div>

  renderTerms: ->
    terms = _.map(@state.filteredTerms, (term) =>
      flashedClassName = "flashed flashed-#{@state.flashedColor}" if term.id == @state.flashedTermId

      <Term key={term.id}
            term={term}
            query={@state.query}
            flashedClassName={flashedClassName}
            sourceRightToLeft={@props.project.sourceLanguage.rightToLeft}
            targetRightToLeft={@state.targetLanguage.rightToLeft}
            editTerm={@editTerm}
            deleteTerm={@deleteTerm} />
    )

    <div className="glossary-terms" id="glossary-terms">
      <div className="list-group">
        {terms}
      </div>
    </div>

  renderTermForm: ->
    if @state.editedTermId != null
      <TermForm term={@editedTerm() || {}}
                newSource={@state.newSource}
                sourceLanguage={@props.project.sourceLanguage}
                targetLanguage={@state.targetLanguage}
                sourceRightToLeft={@props.project.sourceLanguage.rightToLeft}
                targetRightToLeft={@state.targetLanguage.rightToLeft}
                saveTerm={@saveTerm}
                closeTerm={@closeTerm} />
